import { useMemo, useState } from 'react';
import { AlertTriangle, ArrowRight, ChevronDown, Link2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/settings/settings-controls';
import { SubcategoryPicker } from './transition-events-section';
import { LINK_MODES } from '@/lib/queues';
import { cn } from '@/lib/utils';

// firstPlacementOf mirrors the backend's firstPlacement: the first swimlane's first sub-category.
function firstPlacementOf(queue) {
  for (const lane of Array.isArray(queue?.swimlanes) ? queue.swimlanes : []) {
    const sub = (lane.subCategories || [])[0];
    if (sub) return { laneId: lane.id, subId: sub.id };
  }
  return { laneId: '', subId: '' };
}

function placementResolves(queue, laneId, subId) {
  if (!laneId || !subId) return false;
  const lane = (queue?.swimlanes || []).find((l) => l.id === laneId);
  return Boolean(lane && (lane.subCategories || []).some((s) => s.id === subId));
}

function QueueSelect({ value, onChange, disabled, options }) {
  const [open, setOpen] = useState(false);
  const selected = options.find((q) => q.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="inline-flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3 text-[13px] font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Link2 className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          <span className="max-w-56 truncate">{selected ? selected.name || 'Untitled queue' : 'Pick a queue'}</span>
          <ChevronDown className="ml-1 h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-1.5">
        {options.map((q) => (
          <button
            key={q.id}
            type="button"
            onClick={() => {
              onChange(q.id);
              setOpen(false);
            }}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-accent',
              value === q.id ? 'bg-accent font-semibold' : '',
            )}
          >
            <Link2 className="h-3.5 w-3.5 text-primary" aria-hidden />
            <span className="flex-1 truncate">{q.name || 'Untitled queue'}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function ModeToggle({ value, onChange, disabled }) {
  return (
    <div className="inline-flex rounded-md border border-input bg-background p-0.5">
      {LINK_MODES.map((mode) => (
        <button
          key={mode.id}
          type="button"
          disabled={disabled}
          onClick={() => onChange(mode.id)}
          className={cn(
            'rounded px-3 py-1.5 text-[12.5px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed',
            value === mode.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {mode.label}
        </button>
      ))}
    </div>
  );
}

function LinkRow({ label, children }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="w-56 shrink-0 text-[12.5px] font-semibold text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

// The Queue links tab: connect a sub-category to another queue of the same clinic. Entering the
// sub-category then either MOVES the tile to the target queue ("transfer") or keeps it here while
// a linked copy works through the target queue ("mirror" / show in both). Modeled on
// TransitionEventsSection — same SubcategoryPicker-driven selection, one editor card per sub.
export function QueueLinksSection({
  swimlanes,
  linkableQueues,
  queueNameById,
  clinicAttached,
  disabled = false,
  onSetLink,
}) {
  const firstLane = swimlanes[0];
  const firstSub = firstLane?.subCategories?.[0];
  const [selection, setSelection] = useState(() => ({
    laneId: firstLane?.id ?? null,
    subId: firstSub?.id ?? null,
  }));

  const { lane, sub } = useMemo(() => {
    const resolvedLane =
      swimlanes.find((l) => l.id === selection.laneId && l.subCategories?.length) ||
      swimlanes.find((l) => l.subCategories?.length) ||
      null;
    const resolvedSub =
      resolvedLane?.subCategories?.find((s) => s.id === selection.subId) ||
      resolvedLane?.subCategories?.[0] ||
      null;
    return { lane: resolvedLane, sub: resolvedSub };
  }, [swimlanes, selection]);

  const configuredLinks = useMemo(() => {
    const out = [];
    for (const l of swimlanes) {
      for (const s of l.subCategories || []) {
        if (s.linkedQueue) out.push({ lane: l, sub: s, link: s.linkedQueue });
      }
    }
    return out;
  }, [swimlanes]);

  if (!clinicAttached) {
    return (
      <div className="rounded-md border border-dashed border-border px-3 py-8 text-center text-sm text-muted-foreground">
        Attach this queue to a clinic to link statuses to the clinic&apos;s other queues.
      </div>
    );
  }
  if (!lane || !sub) {
    return (
      <div className="rounded-md border border-dashed border-border px-3 py-8 text-center text-sm text-muted-foreground">
        Add a swimlane with at least one sub-category to configure queue links.
      </div>
    );
  }

  const link = sub.linkedQueue || null;
  const targetQueue = link ? linkableQueues.find((q) => q.id === link.queueId) || null : null;
  const targetDangling = Boolean(link) && !targetQueue;
  const landingDangling =
    Boolean(link) && targetQueue && !placementResolves(targetQueue, link.targetSwimlaneId, link.targetSubCategoryId);
  const isMirror = link?.mode === 'mirror';

  const seedLink = () => {
    const target = linkableQueues[0];
    if (!target) return;
    const landing = firstPlacementOf(target);
    onSetLink(lane.id, sub.id, {
      queueId: target.id,
      mode: 'transfer',
      targetSwimlaneId: landing.laneId,
      targetSubCategoryId: landing.subId,
      returnSwimlaneId: '',
      returnSubCategoryId: '',
    });
  };
  const patchLink = (patch) => onSetLink(lane.id, sub.id, { ...link, ...patch });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[12.5px] font-semibold text-muted-foreground">Sub-category</span>
        <SubcategoryPicker
          swimlanes={swimlanes}
          value={{ laneId: lane.id, subId: sub.id }}
          onChange={setSelection}
        />
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-card px-4 py-3.5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[13px] font-extrabold text-foreground">Link this status to another queue</div>
            <div className="text-[11.5px] text-muted-foreground">
              Patients entering this status are routed into the target queue.
            </div>
          </div>
          <Switch
            checked={Boolean(link)}
            onChange={(v) => (v ? seedLink() : onSetLink(lane.id, sub.id, null))}
            label="Link this status to another queue"
            disabled={disabled || (!link && linkableQueues.length === 0)}
          />
        </div>

        {!link && linkableQueues.length === 0 ? (
          <div className="rounded-md border border-dashed border-border px-3 py-4 text-center text-[12.5px] text-muted-foreground">
            This clinic has no other queues yet. Create a second queue for this clinic to enable links.
          </div>
        ) : null}

        {link && targetDangling ? (
          <div className="flex items-center gap-2.5 rounded-md bg-amber-500/15 px-3 py-2.5 text-[12.5px] font-semibold text-amber-600 ring-1 ring-amber-500/40 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
            <span className="flex-1">
              Target queue is missing or belongs to another clinic — this link is inactive.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onSetLink(lane.id, sub.id, null)}
              disabled={disabled}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Remove link
            </Button>
          </div>
        ) : null}

        {link && !targetDangling ? (
          <div className="space-y-3">
            <LinkRow label="Target queue">
              <QueueSelect
                value={link.queueId}
                options={linkableQueues}
                disabled={disabled}
                onChange={(queueId) => {
                  const next = linkableQueues.find((q) => q.id === queueId);
                  const landing = firstPlacementOf(next);
                  patchLink({
                    queueId,
                    targetSwimlaneId: landing.laneId,
                    targetSubCategoryId: landing.subId,
                  });
                }}
              />
            </LinkRow>
            <LinkRow label="When a patient enters this status">
              <div className="flex flex-col gap-1">
                <ModeToggle value={link.mode} onChange={(mode) => patchLink({ mode })} disabled={disabled} />
                <span className="text-[11.5px] text-muted-foreground">
                  {LINK_MODES.find((m) => m.id === link.mode)?.hint}
                </span>
              </div>
            </LinkRow>
            <LinkRow label={`Landing status in ${targetQueue.name || 'target queue'}`}>
              <div className="flex flex-col gap-1">
                <SubcategoryPicker
                  swimlanes={targetQueue.swimlanes || []}
                  value={{ laneId: link.targetSwimlaneId, subId: link.targetSubCategoryId }}
                  disabled={disabled}
                  onChange={({ laneId, subId }) =>
                    patchLink({ targetSwimlaneId: laneId || '', targetSubCategoryId: subId || '' })
                  }
                />
                {landingDangling ? (
                  <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                    Landing status no longer exists in {targetQueue.name || 'the target queue'} — pick a new
                    one (until then patients land on its first status).
                  </span>
                ) : null}
              </div>
            </LinkRow>
            {isMirror ? (
              <LinkRow label="Return status when the linked copy is done">
                <div className="flex flex-col gap-1">
                  <SubcategoryPicker
                    swimlanes={swimlanes}
                    value={{ laneId: link.returnSwimlaneId || null, subId: link.returnSubCategoryId || null }}
                    disabled={disabled}
                    allowNone
                    noneLabel="Stay in place"
                    onChange={({ laneId, subId }) =>
                      patchLink({ returnSwimlaneId: laneId || '', returnSubCategoryId: subId || '' })
                    }
                  />
                  <span className="text-[11.5px] text-muted-foreground">
                    When the copy is marked done in {targetQueue.name || 'the target queue'}, this tile
                    moves here automatically.
                  </span>
                </div>
              </LinkRow>
            ) : null}
          </div>
        ) : null}
      </div>

      {configuredLinks.length > 0 ? (
        <div className="space-y-1.5">
          <span className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">
            Configured links
          </span>
          <div className="overflow-hidden rounded-lg border border-border">
            {configuredLinks.map(({ lane: l, sub: s, link: lk }) => {
              const name = queueNameById?.get(lk.queueId);
              const dangling = !linkableQueues.some((q) => q.id === lk.queueId);
              return (
                <button
                  key={`${l.id}:${s.id}`}
                  type="button"
                  onClick={() => setSelection({ laneId: l.id, subId: s.id })}
                  className={cn(
                    'flex w-full items-center gap-2 border-b border-border bg-card px-3 py-2 text-left text-[12.5px] last:border-b-0 hover:bg-accent',
                    l.id === lane.id && s.id === sub.id ? 'bg-accent' : '',
                  )}
                >
                  <span className="font-semibold text-foreground">{l.name || 'Untitled'}</span>
                  <span className="text-muted-foreground">›</span>
                  <span className="font-semibold text-foreground">{s.name || 'Untitled'}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                  <span className="text-muted-foreground">
                    {lk.mode === 'mirror' ? 'Show in both' : 'Move to'}
                  </span>
                  <span className={cn('font-semibold', dangling ? 'text-amber-600 dark:text-amber-400' : 'text-foreground')}>
                    {name || 'missing queue'}
                  </span>
                  {dangling ? (
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" aria-hidden />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
        <Link2 className="h-3.5 w-3.5" aria-hidden />
        Returning patients are never duplicated — if they already have a tile in the target queue, that
        tile moves instead.
      </div>
    </div>
  );
}
